import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
} from '@mui/material';
import {
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ResponsivePageSection from '../components/responsive/ResponsivePageSection';
import PersonnelHoursHistoryList from '../components/PersonnelHoursHistoryList';
import HoursEntryEditDot from '../components/HoursEntryEditDot';
import { useAuth } from '../context/AuthContext';
import { getMyPersonnelHours } from '../services/api';
import { formatDateEuropean, rangeStringsForViewMonth } from '../utils/dateUtils';
import { canAccess } from '../utils/permissions';
import { formatPersonnelName } from '../utils/personnelDisplay';
import { formatHoursEntryMeta } from '../utils/hoursEntryDisplay';

const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function formatHours(hours) {
  const n = Number(hours);
  if (Number.isNaN(n)) return '0';
  return (Math.round(n * 10) / 10).toFixed(1).replace(/\.0$/, '');
}

function getCurrentViewMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function MyHours() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [viewMonth, setViewMonth] = useState(getCurrentViewMonth);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [expandedEntryKeys, setExpandedEntryKeys] = useState({});

  const hasAccess = canAccess(user, 'myHours.read') && user?.personnel;

  const { fromStr, toStr } = useMemo(() => rangeStringsForViewMonth(viewMonth), [viewMonth]);
  const [y, m] = viewMonth.split('-').map(Number);
  const monthLabel = `${monthNames[m - 1]} ${y}`;

  const loadReport = useCallback(async () => {
    if (!hasAccess || !fromStr || !toStr) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getMyPersonnelHours({ from: fromStr, to: toStr });
      setReport(res.data);
      setExpandedEntryKeys({});
    } catch (err) {
      setReport(null);
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [hasAccess, fromStr, toStr]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handleViewMonthChange = (delta) => {
    const [year, month] = viewMonth.split('-').map(Number);
    const d = new Date(year, month - 1 + delta, 1);
    setViewMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const toggleEntryExpand = (entryId) => {
    setExpandedEntryKeys((prev) => ({ ...prev, [entryId]: !prev[entryId] }));
  };

  if (!canAccess(user, 'myHours.read')) {
    return (
      <ResponsivePageSection>
        <Alert severity="warning">{t('myHours.noPermission')}</Alert>
      </ResponsivePageSection>
    );
  }

  if (!user?.personnel) {
    return (
      <ResponsivePageSection>
        <Typography variant="h4" gutterBottom>{t('myHours.title')}</Typography>
        <Alert severity="info">{t('myHours.notLinked')}</Alert>
      </ResponsivePageSection>
    );
  }

  return (
    <ResponsivePageSection>
      <Typography variant="h4" gutterBottom>
        {t('myHours.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('myHours.subtitle')}
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <IconButton onClick={() => handleViewMonthChange(-1)} aria-label={t('myHours.previousMonth')}>
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="h6">{monthLabel}</Typography>
            <IconButton onClick={() => handleViewMonthChange(1)} aria-label={t('myHours.nextMonth')}>
              <ChevronRightIcon />
            </IconButton>
          </Box>

          {report && (
            <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Chip
                label={`${t('myHours.totalHours')}: ${formatHours(report.totalHours)}`}
                color="primary"
              />
              <Typography variant="body2" color="text.secondary">
                {formatPersonnelName(report.personnel)}
              </Typography>
            </Box>
          )}

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {error && !loading && (
            <Alert severity="error">{error}</Alert>
          )}

          {!loading && !error && report && (
            <>
              {!report.entries?.length ? (
                <Alert severity="info">{t('myHours.noData')}</Alert>
              ) : (
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell width={48} />
                        <TableCell>{t('myHours.date')}</TableCell>
                        <TableCell>{t('myHours.project')}</TableCell>
                        <TableCell align="right">{t('myHours.hours')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {report.entries.map((entry) => (
                        <React.Fragment key={entry.id}>
                          <TableRow hover>
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={() => toggleEntryExpand(entry.id)}
                                aria-label={expandedEntryKeys[entry.id]
                                  ? t('myHours.collapseHistory')
                                  : t('myHours.expandHistory')}
                              >
                                {expandedEntryKeys[entry.id] ? <CollapseIcon /> : <ExpandIcon />}
                              </IconButton>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                                {formatDateEuropean(entry.date)}
                                <HoursEntryEditDot hasEdits={entry.hasEdits} />
                              </Box>
                              {formatHoursEntryMeta(entry, t).map((line) => (
                                <Typography key={line} variant="caption" color="text.secondary" display="block">
                                  {line}
                                </Typography>
                              ))}
                            </TableCell>
                            <TableCell>{entry.project?.name || '—'}</TableCell>
                            <TableCell align="right">{formatHours(entry.hours)}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={4} sx={{ py: 0, borderBottom: expandedEntryKeys[entry.id] ? undefined : 0 }}>
                              <Collapse in={expandedEntryKeys[entry.id]} timeout="auto" unmountOnExit>
                                <Box sx={{ py: 1, pl: 2 }}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    {t('myHours.changeHistory')}
                                  </Typography>
                                  <PersonnelHoursHistoryList entryId={entry.id} />
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </ResponsivePageSection>
  );
}

export default MyHours;
